import {
  Button,
  ButtonGroup,
  Checkbox,
  AccordionCard,
  AccordionCardGroup,
  FieldGroup,
  Pagination,
} from '@ver-uds/react';
import { Account, AccountStatus, AccountType, CompanyInfo, SortDirection, SortField } from 'eve-account-client';
import { useForm } from 'react-hook-form';
import React, {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import LoaderIcon from 'uswds/dist/img/loader.svg';
import { Select, Label, Icon, Alert, CardGroup, Card } from '@ver-uds/uswds-react';
import { generatePath, Link, useHistory } from 'react-router-dom';
import { format as formatDate } from 'date-fns';
import { EveRoutes } from '@ver-uds/e-verify';
import classNames from 'classnames';
import { useAccountStatusUpdateMutation, useResendMouEmailMutation, useSearchAccountQuery } from '../../services';
import ManageCompaniesContext from '../../context/ManageCompanies/ManageCompaniesContext';
import type { CloseAccountPageLocationState } from '../../pages/CloseAccountPage';
import { RejectEnrollmentModal } from './RejectEnrollmentModal';
import { AlertType, RejectEnrollmentModalData, ResendMouEmailModalData } from './ManageCompaniesResultsModalData';
import { ResendMouEmailModal } from './ResendMouEmailModal';
import AccountStatusTag from './AccountStatusTag';
import CancelTerminationSuccessAlert from '../CancelTerminationSuccessAlert/CancelTerminationSuccessAlert';

export type ManageCompaniesResultsProps = {
  accountType: AccountType;
};

function renderStreetAddress(companyInfo: CompanyInfo): string {
  const { line1, line2 } = companyInfo.physicalLocation;

  return `${line1}${line2 ? ` ${line2}` : ''}`;
}

function renderCityStateZip(companyInfo: CompanyInfo): string {
  const { city, stateCode, zipCode } = companyInfo.physicalLocation;

  return `${city}, ${stateCode} ${zipCode}`;
}

function renderArrowIcon(sortDirection: SortDirection): JSX.Element {
  const isAscending = sortDirection === SortDirection.ASC;

  return (
    <Icon
      name={isAscending ? 'arrow_upward' : 'arrow_downward'}
      title={{ id: 'sort-direction', text: isAscending ? 'Ascending' : 'Descending' }}
      size={3}
    />
  );
}

type FormValues = {
  account: string | string[];
};

const ManageCompaniesResults = ({ accountType }: ManageCompaniesResultsProps): JSX.Element => {
  const history = useHistory<CloseAccountPageLocationState>();

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { register, unregister, reset, handleSubmit, errors } = useForm<FormValues>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    shouldFocusError: false,
    defaultValues: {
      account: undefined,
    },
  });

  // Resets the form state.
  // Run this before and afer the rendered checkboxes change.
  const resetFormState = useCallback(() => {
    reset({
      account: undefined,
    });
    unregister('account');
  }, [reset, unregister]);

  const {
    query,
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
    pageSize,
    setPageSize,
    pageIndex,
    setPageIndex,
  } = useContext(ManageCompaniesContext);

  const { resolvedData, isLoading, isFetching, error, refetch } = useSearchAccountQuery(
    query,
    [{ field: sortField, order: sortDirection }],
    {
      page: pageIndex,
      size: pageSize,
    },
    {
      onSettled() {
        // Run before(?) the rendered checkboxes change.
        resetFormState();
      },
    },
  );
  const numberOfResults = resolvedData?.paginated?.totalElements || 0;
  const offset = resolvedData?.paginated?.offset || 0;
  const pageCount = resolvedData?.paginated?.totalPages || 0;

  const accounts = useMemo(() => resolvedData?.paginated?.data ?? [], [resolvedData]);
  const accountsById = new Map<string, Account>(
    accounts.map((accountToMap) => [accountToMap.id as string, accountToMap]),
  );

  const resultsTitleSingular =
    accountType === AccountType.CORPORATE ? ' Company Location Found' : ' Client Company Found';

  const resultsTitlePlural =
    accountType === AccountType.CORPORATE ? ' Company Locations Found' : ' Client Companies Found';

  const terminateSubmitLink =
    accountType === AccountType.CORPORATE
      ? EveRoutes.CORPORATE_COMPANY_LOCATIONS_CLOSE
      : EveRoutes.COMPANY_CLIENTS_CLOSE;

  const addNewCompanyLink =
    accountType === AccountType.CORPORATE ? EveRoutes.CORPORATE_COMPANY_LOCATIONS_NEW : EveRoutes.COMPANY_CLIENTS_NEW;

  const viewCompanyProfileLink =
    accountType === AccountType.CORPORATE
      ? EveRoutes.CORPORATE_COMPANY_LOCATIONS_CHILD_PROFILE_VIEW
      : EveRoutes.COMPANY_CLIENTS_CLIENT_PROFILE_VIEW;

  // Run after(?) the rendered checkboxes change.
  // Depends on `accounts` since changes to `accounts` will change what checkboxes are rendered.
  useLayoutEffect(() => {
    resetFormState();
  }, [resetFormState, accounts]);

  // this looks for the condition where we've ended up with 0 rows on the current page through deletion or changing the page size
  useEffect(() => {
    if (!isFetching && offset >= numberOfResults && offset !== 0) {
      setPageIndex(Math.floor(numberOfResults / offset));
    }
  }, [isFetching, offset, numberOfResults, setPageIndex]);

  // Note: the below element is rerendered in a useEffect manually to avoid triggering that useEffect on every render
  // React docs state useMemo should only be used for optimization because React may reset variable to clear up memory
  const selectAllElement = (
    <span className="text-bold" aria-live="polite">
      Select All
    </span>
  );
  const unselectAllElement = (
    <span className="text-bold" aria-live="polite">
      Unselect All
    </span>
  );

  const [selectAllSpan, setSelectAllSpan] = useState(selectAllElement);
  const checkAllCheckbox = useRef<HTMLInputElement | null>(null);
  const accountCheckboxes = useRef<Array<HTMLInputElement | null>>([]);

  // Reject Enrollment Modal
  const [rejectEnrollmentModalOpen, setRejectEnrollmentModalOpen] = useState<boolean>(false);
  const [rejectEnrollmentData, setRejectEnrollmentData] = useState<RejectEnrollmentModalData | undefined>(undefined);
  const [rejectEnrollmentAlert, setRejectEnrollmentAlert] = useState<AlertType | undefined>(undefined);

  // Resend MOU Email Modal
  const [resendMouEmailModalOpen, setResendMouEmailModalOpen] = useState<boolean>(false);
  const [resendMouEmailData, setResendMouEmailData] = useState<ResendMouEmailModalData | undefined>(undefined);
  const [resendMouEmailAlert, setResendMouEmailAlert] = useState<AlertType | undefined>(undefined);

  // clears all account checkmarks any time a new query is fired. keeps "select all" from misbehaving
  useEffect(() => {
    setSelectAllSpan(
      <span className="text-bold" aria-live="polite">
        Select All
      </span>,
    );
    if (checkAllCheckbox.current) {
      checkAllCheckbox.current.checked = false;
      checkAllCheckbox.current.indeterminate = false;
    }
    // eslint-disable-next-line no-unused-expressions
    accountCheckboxes.current.forEach((checkbox) => {
      if (checkbox) {
        // eslint-disable-next-line no-param-reassign
        checkbox.checked = false;
      }
    });
  }, [accounts]);

  const handleCheckboxChanges = (e: ChangeEvent<HTMLInputElement>) => {
    const { target } = e;
    if (target.id === 'select-all') {
      if (checkAllCheckbox.current) {
        checkAllCheckbox.current.indeterminate = false;
      }
      if ((target as HTMLInputElement).checked) {
        setSelectAllSpan(unselectAllElement);
        accountCheckboxes.current.forEach((checkbox) => {
          if (checkbox) {
            // eslint-disable-next-line no-param-reassign
            checkbox.checked = true;
          }
        });
      } else {
        setSelectAllSpan(selectAllElement);
        accountCheckboxes.current.forEach((checkbox) => {
          if (checkbox) {
            // eslint-disable-next-line no-param-reassign
            checkbox.checked = false;
          }
        });
      }
    } else {
      const checkboxesAccountsDiff =
        accounts.length -
        accountCheckboxes.current.filter((checkbox) => {
          return checkbox?.checked;
        }).length;
      if (checkboxesAccountsDiff === 0) {
        setSelectAllSpan(unselectAllElement);
        if (checkAllCheckbox.current) {
          checkAllCheckbox.current.checked = true;
          checkAllCheckbox.current.indeterminate = false;
        }
      } else if (checkboxesAccountsDiff > 0 && checkboxesAccountsDiff < accounts.length) {
        setSelectAllSpan(selectAllElement);
        if (checkAllCheckbox.current) {
          checkAllCheckbox.current.checked = false;
          checkAllCheckbox.current.indeterminate = true;
        }
      } else {
        setSelectAllSpan(selectAllElement);
        if (checkAllCheckbox.current) {
          checkAllCheckbox.current.checked = false;
          checkAllCheckbox.current.indeterminate = false;
        }
      }
    }
  };

  const renderTerminationActions = (account: Account): JSX.Element | null => {
    switch (account.status) {
      case AccountStatus.ACTIVE:
      case AccountStatus.TERMINATIONREQUESTED:
        return (
          <Link<CloseAccountPageLocationState>
            to={{
              pathname: terminateSubmitLink,
              state: { selectedAccounts: [account] },
            }}
          >
            {account.status === AccountStatus.ACTIVE ? 'Terminate' : 'Manage Termination Request'}
          </Link>
        );
      default:
        return null;
    }
  };

  const [mutateUpdate] = useAccountStatusUpdateMutation((updateError: Error | Response) => {
    setRejectEnrollmentAlert(AlertType.ERROR);
  });
  const rejectEnrollment = async (accountId: string | undefined) => {
    if (accountId) {
      try {
        await mutateUpdate({
          accountId,
          status: AccountStatus.REJECTED,
          closingInfo: {
            closedDate: new Date(),
            closedReason: 'Rejected by Employer Agent',
          },
        });
        setRejectEnrollmentAlert(AlertType.SUCCESS);
      } catch (updateError) {
        // todo
      }
    }
  };

  const [mutateResendEmail] = useResendMouEmailMutation((resendError: Error | Response) => {
    setResendMouEmailAlert(AlertType.ERROR);
  });
  const resendMouEmail = async (accountId: string | undefined) => {
    if (accountId) {
      try {
        await mutateResendEmail({
          accountId,
        });
        setResendMouEmailAlert(AlertType.SUCCESS);
      } catch (updateError) {
        // todo
      }
    }
  };

  const renderResendMouEmail = (clientAccount: Account): JSX.Element | null => {
    if (
      accountType === AccountType.AGENCY &&
      clientAccount.type === AccountType.CLIENT &&
      [AccountStatus.PENDING, AccountStatus.REJECTED].includes(clientAccount.status)
    ) {
      return (
        <>
          <Button
            className="border-none"
            variant="tertiary"
            type="button"
            onClick={() => {
              if (clientAccount.id && clientAccount.companyInfo.companyName) {
                setResendMouEmailData({
                  id: clientAccount.id,
                  companyName: clientAccount.companyInfo.companyName,
                  status: clientAccount.status,
                });
                setResendMouEmailModalOpen(true);
              }
            }}
          >
            <p className="text-normal text-underline">Resend MOU Email</p>
          </Button>
        </>
      );
    }
    return null;
  };

  const renderRejectEnrollment = (clientAccount: Account): JSX.Element | null => {
    if (
      accountType === AccountType.AGENCY &&
      clientAccount.type === AccountType.CLIENT &&
      clientAccount.status === AccountStatus.PENDING
    ) {
      return (
        <>
          <Button
            className="border-none"
            variant="tertiary"
            type="button"
            onClick={() => {
              if (clientAccount.id && clientAccount.companyInfo.companyName) {
                setRejectEnrollmentData({ id: clientAccount.id, companyName: clientAccount.companyInfo.companyName });
                setRejectEnrollmentModalOpen(true);
              }
            }}
          >
            <p className="text-normal text-underline">Reject Enrollment</p>
          </Button>
        </>
      );
    }
    return null;
  };

  function renderResultsSection(): JSX.Element {
    if (error) {
      return (
        <>
          <Alert status="error" slim>
            <Alert.Text>Unable to retrieve data at this time. Refresh to try again</Alert.Text>
          </Alert>
          <CardGroup className="margin-top-3 flex-12">
            <Card className="margin-x-auto flex-fill">
              <Card.Body className="flex-fill bg-base-lightest text-baseline height-10">
                <p className="text-center">No Companies to Display</p>
                <p className="text-center">
                  <Button type="button" variant="tertiary" className="text-middle" onClick={() => refetch()}>
                    <Icon
                      className="font-ui-lg line-height-ui-3 text-primary"
                      name="loop"
                      title={{ id: 'refresh-search-results', text: 'Refresh Search Results' }}
                    />
                    <div className="margin-left-2px padding-top-05">Refresh</div>
                  </Button>
                </p>
              </Card.Body>
            </Card>
          </CardGroup>
        </>
      );
    }
    if (numberOfResults === 0) {
      if (
        Object.entries(query).filter(([, queryParameter]) =>
          Array.isArray(queryParameter) ? queryParameter.length > 0 : queryParameter,
        ).length > 0
      ) {
        return (
          <>
            <Alert status="info" slim>
              <Alert.Text>
                No results found. Try adjusting your search or filters to find what you are looking for.
              </Alert.Text>
            </Alert>
            <CardGroup className="margin-top-3 flex-12">
              <Card className="margin-x-auto flex-fill">
                <Card.Body className="flex-fill bg-base-lightest text-baseline height-10">
                  <p className="text-center">No Companies to Display</p>
                </Card.Body>
              </Card>
            </CardGroup>
          </>
        );
        // eslint-disable-next-line no-else-return
      } else {
        return (
          <>
            <Alert status="info" slim>
              <Alert.Text>There are no Companies to display. Add a new Company to get started.</Alert.Text>
            </Alert>
            <CardGroup className="margin-top-3 flex-12">
              <Card className="margin-x-auto flex-fill">
                <Card.Body className="flex-fill bg-base-lightest text-baseline height-10">
                  <p className="text-center">No Companies to Display</p>
                </Card.Body>
              </Card>
            </CardGroup>
          </>
        );
      }
    }

    return (
      <AccordionCardGroup className="margin-top-3">
        {accounts.map((account) => {
          // All accounts should have IDs at this point
          const accountId = account.id as string;

          const commonFieldClassNames = ['grid-col-12', 'tablet:grid-col-6', 'desktop:grid-col-3'];

          // reset accountCheckboxes array every time this component is rendered. Otherwise Select All functionality basically doesn't work.
          accountCheckboxes.current = [];

          return (
            <AccordionCard controls={`more-details-for-${accountId}`} key={accountId}>
              <AccordionCard.CheckboxHeader
                id={`checkbox-header-${accountId}`}
                name="account"
                value={accountId}
                ref={(element) => {
                  register(element, {
                    required: {
                      value: true,
                      message: 'You must select a company to terminate.',
                    },
                    validate: {
                      allActive: (accountIds: FormValues['account']) => {
                        const selectedIds: string[] = Array.isArray(accountIds) ? accountIds : [accountIds];

                        if (selectedIds.every((id) => accountsById.get(id)?.status === AccountStatus.ACTIVE)) {
                          return true;
                        }
                        return 'At least one of your selected companies cannot be terminated. Please only select companies that are active.';
                      },
                    },
                  });
                  accountCheckboxes.current.push(element);
                }}
                label={account.companyInfo?.companyName}
                className="grid-row grid-gap desktop:flex-no-wrap"
                onChange={handleCheckboxChanges}
              >
                <div className="grid-col-12 tablet:grid-col-auto tablet:flex-full-auto-noshrink grid-gap flex-align-baseline margin-top-1 tablet:margin-top-0">
                  <AccountStatusTag status={account.status} />
                </div>
                <div className="grid-col-12 tablet:grid-col-auto tablet:flex-full-auto-noshrink grid-row grid-gap desktop:flex-justify-end flex-align-baseline margin-top-3 desktop:margin-top-0">
                  <Link
                    to={generatePath(viewCompanyProfileLink, {
                      subAccountId: accountId,
                    })}
                  >
                    View Company Profile
                  </Link>
                  {renderRejectEnrollment(account)}
                  {renderTerminationActions(account)}
                  {renderResendMouEmail(account)}
                </div>
              </AccordionCard.CheckboxHeader>
              <AccordionCard.Body>
                <FieldGroup className="grid-row grid-gap margin-y-0">
                  <FieldGroup.Field
                    // Only on desktop will this field will be part of the last rendered row.
                    className={classNames(commonFieldClassNames, ['desktop:margin-bottom-0'])}
                    name="Street Address"
                  >
                    {renderStreetAddress(account.companyInfo)}
                  </FieldGroup.Field>
                  <FieldGroup.Field
                    // Only on desktop will this field will be part of the last rendered row.
                    className={classNames(commonFieldClassNames, ['desktop:margin-bottom-0'])}
                    name="City, State, and Zip"
                  >
                    {renderCityStateZip(account.companyInfo)}
                  </FieldGroup.Field>
                  <FieldGroup.Field
                    // On tablet (and desktop), this field will be part of the last rendered row.
                    className={classNames(commonFieldClassNames, ['tablet:margin-bottom-0'])}
                    name="Company ID"
                  >
                    {accountId}
                  </FieldGroup.Field>
                  <FieldGroup.Field
                    // On all breakpoints, this field will be part of the last rendered row; it shouldn't have a bottom margin.
                    className={classNames(commonFieldClassNames, ['margin-bottom-0'])}
                    name="Unique Entity Identifier (UEI)"
                  >
                    {account.companyInfo.ueiSAM ?? '---'}
                  </FieldGroup.Field>
                </FieldGroup>
              </AccordionCard.Body>
              <AccordionCard.MoreDetails id={`more-details-for-${accountId}`}>
                <FieldGroup className="grid-row grid-gap margin-y-0">
                  <FieldGroup.Field
                    // Only on desktop will this field will be part of the last rendered row.
                    className={classNames(commonFieldClassNames, ['desktop:margin-bottom-0'])}
                    name="MOU Signed Date"
                  >
                    {account.mouSignedDate ? formatDate(account.mouSignedDate, 'MMM d, yyyy') : '---'}
                  </FieldGroup.Field>
                </FieldGroup>
              </AccordionCard.MoreDetails>
            </AccordionCard>
          );
        })}
      </AccordionCardGroup>
    );
  }

  return isLoading ? (
    <div>
      Loading… <img src={LoaderIcon} alt="" className="height-2" />
    </div>
  ) : (
    <>
      <form
        onSubmit={handleSubmit(({ account }) => {
          const selectedIds: string[] = Array.isArray(account) ? account : [account];
          const selectedAccounts: Account[] = selectedIds.map((id) => accountsById.get(id) as Account);

          history.push(terminateSubmitLink, { selectedAccounts });
        })}
        noValidate
      >
        <h2 className="margin-top-4" aria-live="polite">
          {numberOfResults}
          {numberOfResults === 1 ? resultsTitleSingular : resultsTitlePlural}
        </h2>
        <CancelTerminationSuccessAlert />

        {errors.account ? (
          <Alert status="error" slim>
            {errors.account.message}
          </Alert>
        ) : null}

        <div className="grid-row grid-gap margin-top-0 flex-justify">
          <div className="grid-row grid-gap flex-align-baseline">
            <Checkbox
              id="select-all"
              name="select all companies"
              className="margin-top-105 margin-left-3 border-left-2px border-transparent"
              label={selectAllSpan}
              onChange={handleCheckboxChanges}
              ref={checkAllCheckbox}
            />

            <div className="grid-row margin-top-3 flex-align-center flex-no-wrap">
              <Label htmlFor="sort" className="margin-top-0 grid-col-auto">
                Sort by:
              </Label>
              <Select
                id="sort"
                aria-describedby="sort-direction"
                className="grid-col-8 margin-left-105 margin-top-0"
                value={sortField}
                onChange={(event) => setSortField(event.target.value as SortField)}
              >
                <option value={SortField.COMPANYNAME}>Company Name</option>
                <option value={SortField.CITY}>City</option>
                <option value={SortField.STATECODE}>State</option>
                <option value={SortField.ZIPCODE}>Zip</option>
                <option value={SortField.MOUSIGNEDDATE}>MOU Signed Date</option>
                <option value={SortField.CLOSEDREQUESTDATE}>Termination Request Date</option>
                <option value={SortField.CLOSEDDATE}>Rejection/Termination Date</option>
              </Select>
              <Button
                type="button"
                className="margin-left-2 grid-col-auto"
                style={{ marginTop: '0' }}
                variant="tertiary"
                onClick={() =>
                  sortDirection === SortDirection.ASC
                    ? setSortDirection(SortDirection.DESC)
                    : setSortDirection(SortDirection.ASC)
                }
              >
                {renderArrowIcon(sortDirection)}
              </Button>
            </div>
          </div>

          <ButtonGroup className="margin-top-205 grid-col-12 margin-right-0 tablet:grid-col-auto">
            <Button type="submit" variant="secondary">
              Terminate Selected
            </Button>
            <Link className="text-no-underline" to={addNewCompanyLink}>
              <Button type="button" variant="primary" iconLeft={<Icon name="add" />}>
                Add New Company
              </Button>
            </Link>
          </ButtonGroup>
        </div>

        {renderResultsSection()}
        <RejectEnrollmentModal
          open={rejectEnrollmentModalOpen}
          companyData={rejectEnrollmentData}
          modalAlert={rejectEnrollmentAlert}
          onClose={() => {
            setRejectEnrollmentAlert(undefined);
            setRejectEnrollmentModalOpen(false);
          }}
          refetch={() => refetch()}
          onSubmit={() => rejectEnrollment(rejectEnrollmentData?.id)}
        />
        <ResendMouEmailModal
          open={resendMouEmailModalOpen}
          modalData={resendMouEmailData}
          modalAlert={resendMouEmailAlert}
          onClose={() => {
            setResendMouEmailAlert(undefined);
            setResendMouEmailModalOpen(false);
          }}
          onSubmit={() => resendMouEmail(resendMouEmailData?.id)}
        />
      </form>

      {/*
        TODO: Should the pagination be outside the form due to https://git.uscis.dhs.gov/USCIS/ver-uds/issues/495?
        Alternatively, use the new pagination (from @ver-uds/uswds-react?) inside the form.
      */}
      <Pagination
        dataSize={numberOfResults ?? 0}
        pageCount={pageCount}
        pageOptions={Array.from(Array(pageCount).keys())}
        gotoPage={(x): void => {
          setPageIndex(x);
        }}
        previousPage={(): void => {
          setPageIndex((currentPageIndex) => currentPageIndex - 1);
        }}
        nextPage={(): void => {
          setPageIndex((currentPageIndex) => currentPageIndex + 1);
        }}
        setPageSize={(x): void => {
          setPageSize(x);
        }}
        canPreviousPage={pageIndex > 0}
        canNextPage={pageIndex < pageCount - 1}
        pageIndex={pageIndex}
        pageSize={pageSize}
        pageSizeOptions={[10, 25, 50]}
      />
    </>
  );
};

export default ManageCompaniesResults;
